// Base URL resolution. Priority:
//   1. Dev (VITE_DEV): talk to the Vite dev server's OWN origin; vite.config
//      proxies the API route prefixes to this worktree's Go backend. Keeps the
//      browser on a single always-reachable origin — no tunnel, no WSL
//      cross-port reachability problem.
//   2. VITE_PARROT_API_BASE_URL — worktree-isolated backend URL injected by
//      `parrot services up client` (procman.go) for non-dev runs.
//   3. VITE_PARROT_TARGET — standalone deployment on its own domain
//      (app.parrotapp.com) talking cross-origin to the App Platform backend.
//      "app-prod" keeps PROD Stripe/PostHog keys; "app-staging" uses dev keys.
//      Set at image build time in Dockerfile.client.
//   4. Production (legacy): client is embedded in the Go binary and served
//      under /papi/ on the same origin.
// import.meta.env reads live in ./viteEnv so jest (CJS) can mock that module
// instead of parsing `import.meta`.
import {
  VITE_DEV,
  VITE_PARROT_API_BASE_URL,
  VITE_PARROT_TARGET,
  VITE_BASE_URL,
} from "./viteEnv";

let baseUrl;
let oauthUrl;
let key;
let posthogKey;
const devKey =
  "pk_test_51QDV8d3ZherjpZePy3GIbI1zOBLrtrQburFAiKZ3WBjU6wNujwbgjCq8u3in4ONRkyeDQgliFfZwNNu1FGkZb4O800IlMG5azJ";
const prodKey =
  "pk_live_51PrfVqKnijChdbV7Ajx0dqUNpbFpkxmK7n56momlrwDMbzf9oBQffqVgJQJr2Rrt3bKf29ysFkO380LRCgc9vRUJ00jpU5kyS6";
const postHogProdKey = "phc_GOJrt0SMSoxbhiaC2hX1SMEZOPg9MQ5sv8TeStF1JKu";
const postHogDevKey = "phc_dOGXzxSgvPcmR6feFlpjildQxhvOnSzZU2Boh0z9gSz";
const injectedBase = VITE_PARROT_API_BASE_URL;
const deployTarget = VITE_PARROT_TARGET;
if (VITE_DEV) {
  // Dev: talk to the Vite dev server's OWN origin. vite.config proxies the API
  // route prefixes (/auth, /login, /api, /guest, /papi, /health) to this
  // worktree's Go backend. This keeps the browser on a single always-reachable
  // origin — no Cloudflare tunnel, no WSL cross-port reachability problem.
  baseUrl = "/";
  oauthUrl = window.location.origin;
  key = devKey;
  posthogKey = postHogDevKey;
} else if (injectedBase) {
  baseUrl = injectedBase;
  oauthUrl = injectedBase;
  key = devKey;
  posthogKey = postHogDevKey;
} else if (deployTarget === "app-prod") {
  baseUrl = "https://prod.parrotapp.com/";
  oauthUrl = "https://prod.parrotapp.com";
  key = prodKey;
  posthogKey = postHogProdKey;
} else if (deployTarget === "app-staging") {
  baseUrl = "https://staging.parrotapp.com/";
  oauthUrl = "https://staging.parrotapp.com";
  key = devKey;
  posthogKey = postHogDevKey;
} else {
  baseUrl = "/papi/";
  oauthUrl = window.location.origin + "/papi";
  key = prodKey;
  posthogKey = postHogProdKey;
}
// NOT dev since that once release is now prod.
// we will add a new staging env in the future.
if (window && window.location.href.includes("dev.fluentwithparrot.com")) {
  key = prodKey;
  posthogKey = postHogProdKey;
}
if (window && window.location.href.includes("staging.fluentwithparrot.com")) {
  key = devKey;
  posthogKey = postHogDevKey;
}

// Follows the Vite base: "/app/login" on the droplet-embedded build,
// "/login" on the standalone app.parrotapp.com build.
export const BAD_AUTH_REDIRECT_URL = `${VITE_BASE_URL.replace(/\/+$/, "")}/login`;

export const API_BASE_URL = baseUrl;

/**
 * Join API_BASE_URL with a path safely. In dev API_BASE_URL is "/" (same-origin,
 * proxied by Vite), so a naive `${API_BASE_URL}/path` produces `//path` — which
 * the browser treats as a PROTOCOL-RELATIVE URL and resolves to `http://path`
 * (ERR_NAME_NOT_RESOLVED). Strip the trailing slash on the base and the leading
 * slash on the path so the result is always a single-slash join.
 */
export const apiUrl = (path: string): string =>
  `${API_BASE_URL.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
export const OAUTH_REDIRECT_URL = oauthUrl;
export const STRIPE_PUBLISHABLE_KEY = key;
export const POSTHOG_KEY = posthogKey;
